import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Input, Checkbox, Form, Upload, Image, Tabs, Select } from 'antd';
import LoadableButton from "../../../../../components/buttons/LoadableButton.jsx";
import { DepositAmountApi, getBankDataApi } from '../../../../../api/request/wallet/cashdeposit.js';

const CashDeposit = ({ amount, resetAll, loginTerminalId }) => {
    const [form] = Form.useForm();
    const { userDetails } = useSelector(state => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [bankData, setBankData] = useState([])
    const [signatureFileList, setSignatureFileList] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const getBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

    const extractOriginFileObjs = (fileArray) => {
        return fileArray ? fileArray.map((file) => file.originFileObj) : [];
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (!amount) {
                toast.error('Please enter deposit amount');
                return;
            }
            const UploadDocument = extractOriginFileObjs(values?.UploadDocument);
            const isWallet = loginTerminalId === 'wallet';
            let formData = new FormData();
            formData.append("UserId", userDetails?.id);
            formData.append("ReferenceId", values?.ReferenceId);
            formData.append("Comment", values?.Comment || "");
            formData.append("Amount", Number(amount));
            formData.append("loginTerminalId", isWallet ? 0 : loginTerminalId);
            formData.append("DepositType", 3);

            UploadDocument.forEach((file) => formData.append("PaymentProof", file));

            let apiHeaders = { headers: { Accept: "application/json", "Content-Type": "multipart/form-data" } };

            const { data } = await DepositAmountApi(formData, apiHeaders);
            if (data?.success) {
                toast.success(data?.message);
                setSignatureFileList([]);
                form.resetFields();
                resetAll();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Deposit failed.');
        } finally {
            setIsLoading(false);
        }
    }

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleSignatureChange = ({ fileList: newSignatureList }) => {
        setSignatureFileList(newSignatureList);
        form.setFieldsValue({ UploadDocument: newSignatureList });
    };

    const UploadButton = (
        <button type="button">
            <div className='flex items-center justify-center gap-2 border rounded-md py-1.5 px-4 text-lg text-gray-500 font-medium'>
                <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" className='h-5' />
                <span>Click to Upload</span>
            </div>
        </button>
    );

    const getBankDetails = async () => {
        try {
            const { data } = await getBankDataApi();
            setBankData(data.data || []);
            if (data.data?.length > 0) {
                setActiveTab(data.data[0].id.toString());
            }
        } catch (err) {
            console.error(err);
        }
    };


    useEffect(() => {
        getBankDetails()
    }, [])

    const getBankLogoByName = (bankName = '') => {
        const normalizedName = bankName.toLowerCase().trim();

        if (normalizedName.includes('wio')) return '/assets/images/WIO bank limited.png';
        if (normalizedName.includes('ruya')) return '/assets/images/Ruya bank logo.webp';
        if (normalizedName.includes('meezan')) return '/assets/images/meezan.jpg';
        if (normalizedName.includes('habib')) return '/assets/images/Al habibi logo.jpg';

        return null;
    };

    const items =
        bankData?.map((bank) => ({
            key: bank.id.toString(),
            label: (
                <div className="flex items-center gap-2">
                    {getBankLogoByName(bank.bankName) && (
                        <img
                            src={getBankLogoByName(bank.bankName)}
                            alt={bank.bankName}
                            className="w-5 h-5 object-contain rounded-sm"
                        />
                    )}
                    <span>{bank.bankName}</span>
                </div>
            ),
            children: (
                <>
                    <div className="space-y-4 mb-5">
                        <div className='text-lg'>
                            <span className="font-semibold">Bank Name:</span>{" "}
                            <span className="font-medium text-gray-500">{bank.bankName}</span>
                        </div>
                        <div className='text-lg'>
                            <span className="font-semibold text-lg">Account Holder:</span>{" "}
                            <span className="font-medium text-gray-500">
                                {bank.accountHolderName}
                            </span>
                        </div>
                        <div className='text-lg'>
                            <span className="font-semibold text-lg">Account Number:</span>{" "}
                            <span className="font-medium text-gray-500">
                                {bank.accountNumber}
                            </span>
                        </div>
                        <div className='text-lg'>
                            <span className="font-semibold text-lg">IBAN Number:</span>{" "}
                            <span className="font-medium text-gray-500">{bank.ifscCode}</span>
                        </div>
                        <div className='text-lg'>
                            <span className="font-semibold text-lg">Swift Code:</span>{" "}
                            <span className="font-medium text-gray-500">{bank.swiftCode}</span>
                        </div>
                    </div>
                </>
            ),
        })) || [];
    return (
        <div>
            {isMobile ? (
                <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Our Banks</label>
                    <Select
                        placeholder="Choose a bank"
                        size="large"
                        value={activeTab ?? undefined}
                        onChange={setActiveTab}
                        className="w-full"
                        options={bankData?.map((bank) => ({
                            value: bank.id.toString(),
                            label: (
                                <div className="flex items-center gap-2">
                                    {getBankLogoByName(bank.bankName) && (
                                        <img
                                            src={getBankLogoByName(bank.bankName)}
                                            alt={bank.bankName}
                                            className="w-5 h-5 object-contain rounded-sm"
                                        />
                                    )}
                                    <span>{bank.bankName}</span>
                                </div>
                            ),
                        })) ?? []}
                    />
                    {activeTab && bankData?.find((b) => b.id.toString() === activeTab) && (
                        <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
                            {(() => {
                                const bank = bankData.find((b) => b.id.toString() === activeTab);
                                return (
                                    <>
                                        <div className='md:text-lg'>
                                            <span className="font-semibold">Bank Name:</span>{" "}
                                            <span className="font-medium text-gray-500">{bank.bankName}</span>
                                        </div>
                                        <div className='md:text-lg'>
                                            <span className="font-semibold md:text-lg">Account Holder:</span>{" "}
                                            <span className="font-medium text-gray-500">{bank.accountHolderName}</span>
                                        </div>
                                        <div className='md:text-lg'>
                                            <span className="font-semibold md:text-lg">Account Number:</span>{" "}
                                            <span className="font-medium text-gray-500">{bank.accountNumber}</span>
                                        </div>
                                        <div className='md:text-lg'>
                                            <span className="font-semibold md:text-lg">IBAN Number:</span>{" "}
                                            <span className="font-medium text-gray-500">{bank.ifscCode}</span>
                                        </div>
                                        <div className='md:text-lg'>
                                            <span className="font-semibold md:text-lg">Swift Code:</span>{" "}
                                            <span className="font-medium text-gray-500">{bank.swiftCode}</span>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Tabs
                        items={items}
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key)}
                        centered={false}
                        className="w-full custom-tabs min-w-max"
                    />
                </div>
            )}

            <hr />
            <Form form={form} layout="vertical" className='space-y-5 mt-5' onFinish={handleSubmit}>
                <Form.Item
                    name='ReferenceId'
                    label={<span className="text-lg text-gray-500 font-medium">Trasaction ID</span>}
                    rules={[{ required: true, message: 'Please enter trasaction id.' }]}
                >
                    <Input placeholder="Trasaction ID" size='large' />
                </Form.Item>

                <Form.Item
                    name='Comment'
                    label={<span className="text-lg text-gray-500 font-medium">Comment</span>}
                >
                    <Input placeholder="Comment" size='large' />
                </Form.Item>

                <Form.Item
                    name="UploadDocument"
                    label={<span className="text-lg text-gray-500 font-medium">Upload Document</span>}
                    rules={[{ required: true, message: 'Please Upload Document.' }]}
                >
                    <Upload
                        beforeUpload={() => false}
                        listType="picture"
                        className="mt-1"
                        fileList={signatureFileList}
                        onPreview={handlePreview}
                        onChange={handleSignatureChange}
                    >
                        {signatureFileList.length >= 2 ? null : UploadButton}
                    </Upload>
                </Form.Item>

                {previewImage && (
                    <Image
                        src={previewImage}
                        wrapperStyle={{ display: 'none' }}
                        preview={{
                            visible: previewOpen,
                            onVisibleChange: (visible) => setPreviewOpen(visible),
                            afterOpenChange: (visible) => !visible && setPreviewImage(''),
                        }}
                    />
                )}

                <Form.Item
                    name='conditions'
                    valuePropName="checked"
                    rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
                >
                    <Checkbox className='mt-4'>
                        Yes, I agreed to the <Link to={'/terms_&_conditions'} className='primary hover:underline duration-300'>Terms and conditions</Link>
                    </Checkbox>
                </Form.Item>

                <LoadableButton
                    className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover duration-500'
                    type="Submit"
                    lable='Submit'
                    isLoading={isLoading}
                    loadingLable='Adding request...'
                />
            </Form>
        </div>
    )
}

export default CashDeposit
